/**
 * @module app.core
 */

import { autoinject, bindingMode, TaskQueue } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IProviderGroupSearchFilters } from 'app/cd/services/provider-service';
import { ICollapse } from 'app/shared/attributes/ess-collapse-attributes';
import { fireEvent } from 'app/shared/lib/fire-events';
import { guid } from 'app/shared/lib/guid';
import { EventAggregator } from 'aurelia-event-aggregator';
import { SetFocusEvent } from 'app/shared/lib/events';

interface IProviderGroupSearchCustomEvent { providerGroupSearchFilters: IProviderGroupSearchFilters }
export type ProviderGroupSearchCustomEvent = CustomEvent<IProviderGroupSearchCustomEvent>;

@autoinject()
export class ProviderGroupSearchFormCustomElement extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public providerGroupSearchFilters: IProviderGroupSearchFilters = {};

    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public defaultCollapsed: boolean = false;

    protected providerGroupInformationId = guid();
    protected id: guid = guid();

    ////****************** Element References ***************************************////
    private $collapsible!: ICollapse;
    protected $searchCriteriaHeader!: HTMLElement;
    protected $currentCriteriaHeader!: HTMLElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly taskQueue: TaskQueue,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected attached(): void {
        if(this.defaultCollapsed) { return; }

        this.taskQueue.queueTask(() => {
            this.showAvailableCriteria();
        });
    }

    ////****************** Events ***************************************************////
    protected handleSubmit(event?: Event): void {
        if(!this.state.saving && !this.state.loading) {
            this.fireSearch();
        }
    }

    protected handleKeyDown(e: KeyboardEvent): boolean {
        if(e.keyCode === 13 && e.ctrlKey) {
            this.handleSubmit();
            return false;
        }
        return true;
    }

    ////****************** Functions ************************************************////
    public showAvailableCriteria(): void {
        this.$collapsible.open(0);
        this.$collapsible.close(1);
    }

    public showSelectedCriteria(): void {
        this.$collapsible.close(0);
        this.$collapsible.open(1);
    }

    protected fireSearch(): void {
        const detail: IProviderGroupSearchCustomEvent = {
            providerGroupSearchFilters: this.providerGroupSearchFilters
        }
        fireEvent(this.$element, 'search', detail);
        this.showSelectedCriteria();
        this.events.publish(new SetFocusEvent(this.$currentCriteriaHeader));
    }

    public clear(returnFocus?: boolean): void {
        this.providerGroupSearchFilters = {};
        this.showAvailableCriteria();
        fireEvent(this.$element, 'clear-results');
        if(returnFocus) this.events.publish(new SetFocusEvent(this.$searchCriteriaHeader));
    }
}
